/**
 * Building blocks shared by the FS-* boards (area g): chips from the invoicing contracts' metas, quota gauges,
 * KPI tiles, the CSV / JSON export window, date helpers. OWNED BY area (g) — saas.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Download, MoreHorizontal } from "lucide-react";
import {
  CREDIT_NOTE_EINVOICE_META,
  CREDIT_NOTE_STATUS_META,
  EINVOICE_STATUS_META,
  INVOICE_STATUS_META,
  PLAN_META,
  QUOTE_STATUS_META,
  type EInvoiceStatus,
  type InvoicingPlan,
  type StatusMeta,
} from "@/contracts/shared/invoicing/common.ts";
import type { ExportFile, SaasAccountRow, SaasAccountStatus, SaasDocKind, SaasEnrollment } from "@/contracts/areas/saas";
import type { ApiError } from "@/api/client";
import { cn } from "@/lib/cn";
import { formatCents } from "@/lib/format";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Skeleton } from "@/ui/states";
import { Window } from "@/ui/window";
import { useOnline } from "@/ui/offline-strip";

/* ───────────────────────── Dates ───────────────────────── */

/** Calendar parts in Paris time (the back-office's clock, whatever the browser's zone). */
const PARIS = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
function parts(d: Date) {
  const p = Object.fromEntries(PARIS.formatToParts(d).map((x) => [x.type, x.value]));
  return { y: p.year!, m: p.month!, d: p.day!, h: p.hour!, min: p.minute! };
}

/** « aujourd'hui 14:03 » · « hier 16:12 » · « 24/09 14:02 ». */
export function fmtWhen(iso: string, withTime = true): string {
  const p = parts(new Date(iso));
  const now = parts(new Date());
  const yd = parts(new Date(Date.now() - 86_400_000));
  const time = withTime ? ` ${p.h}:${p.min}` : "";
  const key = (x: typeof p) => `${x.y}-${x.m}-${x.d}`;
  if (key(p) === key(now)) return `aujourd'hui${time}`;
  if (key(p) === key(yd)) return `hier${time}`;
  return `${p.d}/${p.m}${p.y !== now.y ? `/${p.y}` : ""}${time}`;
}
/** « 03/10/2027 ». */
export const fmtDate = (iso: string) => {
  if (iso.length === 10) return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  const p = parts(new Date(iso));
  return `${p.d}/${p.m}/${p.y}`;
};
/** « 03/10 ». */
export const fmtDayMonth = (iso: string) => fmtDate(iso).slice(0, 5);

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
/** « Septembre 2026 ». */
export const monthLabel = (ym: string) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
export const monthShort = (ym: string) => MONTHS[Number(ym.slice(5, 7)) - 1]!.toLowerCase();

/** « 6,99 € / mois » from the contract grid. */
export const planPrice = (plan: InvoicingPlan, cycle: "monthly" | "annual") => formatCents(cycle === "monthly" ? PLAN_META[plan].monthlyHtCents : PLAN_META[plan].annualHtCents);

/** « 901 234 567 00021 ». */
export const fmtSiret = (s: string | null) => (s ? s.replace(/^(\d{3})(\d{3})(\d{3})(\d{5})$/, "$1 $2 $3 $4") : "—");

/** « 6,99 € / mois » over « 83,88 € / an » (START: « 0,00 € »). */
export function PlanTariff({ plan }: { plan: InvoicingPlan }) {
  if (plan === "start") return <span className="text-[13px]">{planPrice("start", "monthly")}</span>;
  return (
    <span className="leading-tight">
      <span className="block text-[13px]">{planPrice(plan, "monthly")} / mois</span>
      <span className="block text-[11.5px] text-muted">{planPrice(plan, "annual")} / an</span>
    </span>
  );
}

/* ───────────────────────── Chips ───────────────────────── */

/** Chips may wrap inside a narrow column (phones at 150 % zoom) instead of spilling out. */
const WRAP = "max-w-full whitespace-normal text-left";

export function PlanChip({ plan, className }: { plan: InvoicingPlan; className?: string }) {
  const tone = plan === "start" ? "neutral" : plan === "pro" ? "info" : "dark";
  return (
    <Badge tone={tone} shape="tag" size="sm" className={cn("font-extrabold tracking-[0.03em]", className)}>
      {PLAN_META[plan].label}
    </Badge>
  );
}

export const ACCOUNT_STATUS_META: Record<SaasAccountStatus, StatusMeta> = {
  active: { label: "Actif", tone: "success" },
  quota_reached: { label: "Quota atteint", tone: "warning" },
  restricted: { label: "Accès restreint", tone: "danger" },
  suspended: { label: "Suspendu", tone: "danger" },
};

export function AccountStatusChip({ status }: { status: SaasAccountStatus }) {
  const m = ACCOUNT_STATUS_META[status];
  return (
    <Badge tone={m.tone} size="sm" className={WRAP}>
      {m.label}
    </Badge>
  );
}

export const ENROLLMENT_META: Record<SaasEnrollment, StatusMeta> = {
  registered: { label: "Raccordé", tone: "success" },
  in_progress: { label: "Enrôlement en cours", tone: "blue" },
  not_connected: { label: "Non raccordé", tone: "muted" },
};

export function EnrollmentChip({ enrollment, rejects = 0 }: { enrollment: SaasEnrollment; rejects?: number }) {
  if (rejects > 0)
    return (
      <Badge tone="danger" size="sm" icon={<AlertCircle className="size-3" strokeWidth={2.4} />}>
        {rejects} rejet{rejects > 1 ? "s" : ""}
      </Badge>
    );
  const m = ENROLLMENT_META[enrollment];
  return (
    <Badge tone={m.tone} size="sm" icon={enrollment === "registered" ? <CheckCircle2 className="size-3" strokeWidth={2.4} /> : undefined}>
      {m.label}
    </Badge>
  );
}

/** Pastille « Facture électronique » (feminine labels for invoices, masculine for credit notes). */
export function EinvoiceChip({ status, kind = "invoice" }: { status: EInvoiceStatus | null; kind?: SaasDocKind }) {
  if (!status) return <span className="text-[13px] text-subtle">—</span>;
  const m = (kind === "credit_note" ? CREDIT_NOTE_EINVOICE_META[status] : null) ?? EINVOICE_STATUS_META[status];
  return (
    <Badge tone={m.tone} size="sm" className={WRAP}>
      {m.label}
    </Badge>
  );
}

export function docStatusMeta(kind: SaasDocKind, status: string): StatusMeta {
  const meta = (kind === "invoice" ? INVOICE_STATUS_META : kind === "quote" ? QUOTE_STATUS_META : CREDIT_NOTE_STATUS_META) as Record<string, StatusMeta>;
  return meta[status] ?? { label: status, tone: "neutral" };
}

export function DocStatusChip({ kind, status }: { kind: SaasDocKind; status: string }) {
  const m = docStatusMeta(kind, status);
  return (
    <Badge tone={m.tone} size="sm" className={WRAP}>
      {m.label}
    </Badge>
  );
}

export const DOC_KIND_LABEL: Record<SaasDocKind, string> = { invoice: "Facture", quote: "Devis", credit_note: "Avoir" };

/* ───────────────────────── Quotas ───────────────────────── */

/** 52 × 8 gauge: amber from 80 %, red at the limit; « Illimité » in green without a limit. */
export function QuotaGauge({ used, limit, width = "w-[52px]", plain }: { used: number; limit: number | null; width?: string; /** Seats: full is normal, not an alert. */ plain?: boolean }) {
  if (limit === null) return <span className="text-[12px] font-bold text-success">Illimité</span>;
  const ratio = Math.min(1, used / limit);
  const color = plain ? "bg-primary-500" : used >= limit ? "bg-danger-500" : ratio >= 0.8 ? "bg-warning-500" : "bg-primary-500";
  const text = plain ? "text-primary-700" : used >= limit ? "text-danger" : ratio >= 0.8 ? "text-warning" : "text-primary-700";
  return (
    <span className="inline-flex items-center gap-2">
      <span className={cn("h-2 shrink-0 overflow-hidden rounded-full bg-line", width)} aria-hidden>
        <span className={cn("block h-full rounded-full", color)} style={{ width: `${Math.max(ratio * 100, used > 0 ? 8 : 0)}%` }} />
      </span>
      <span className={cn("text-[12px] font-bold tabular-nums", text)}>
        {used}/{limit}
      </span>
    </span>
  );
}

export function QuotaStack({ quotas }: { quotas: SaasAccountRow["quotas"] }) {
  const rows = [
    ["Factures", quotas.invoices],
    ["Devis", quotas.quotes],
    ["Clients", quotas.customers],
  ] as const;
  return (
    <div className="grid grid-cols-[auto_1fr] items-center gap-x-2.5 gap-y-0.5">
      {rows.map(([label, u]) => (
        <span key={label} className="contents">
          <span className="text-[11.5px] text-muted">{label}</span>
          <QuotaGauge used={u.used} limit={u.limit} />
        </span>
      ))}
    </div>
  );
}

/* ───────────────────────── KPI tiles ───────────────────────── */

const tileIcon = {
  primary: "bg-primary-50 text-primary-500",
  teal: "bg-[#E3F6F3] text-[#0F766E]",
  cyan: "bg-[#E4F5FB] text-[#0E7490]",
  danger: "bg-danger-bg text-danger-500",
  warning: "bg-warning-bg text-warning-500",
  success: "bg-success-bg text-success",
  dark: "bg-white/10 text-lime",
} as const;

export function KpiTile({
  icon,
  tone = "primary",
  value,
  label,
  sub,
  to,
  search,
  variant = "default",
}: {
  icon: ReactNode;
  tone?: keyof typeof tileIcon;
  value: ReactNode;
  label: ReactNode;
  sub?: ReactNode;
  to?: string;
  search?: Record<string, unknown>;
  variant?: "default" | "alert" | "dark";
}) {
  const body = (
    <>
      <span className={cn("inline-flex size-9 items-center justify-center rounded-full [&_svg]:size-4", tileIcon[variant === "dark" ? "dark" : tone])}>{icon}</span>
      <div className={cn("mt-3 text-[28px] font-extrabold leading-none tracking-[-0.02em] tabular-nums", variant === "dark" ? "text-white" : "text-ink")}>{value}</div>
      <div className={cn("mt-2 text-[13px] font-bold leading-snug", variant === "dark" ? "text-white" : "text-ink")}>{label}</div>
      {sub && <div className={cn("mt-1.5 text-[12px] leading-snug", variant === "dark" ? "text-white/70" : "text-muted")}>{sub}</div>}
    </>
  );
  const cls = cn(
    "block min-w-0 rounded-[18px] p-4 transition-[transform,box-shadow] sm:p-[18px]",
    variant === "dark" ? "bg-[linear-gradient(135deg,#1B1C31,#2A2B45)]" : variant === "alert" ? "border border-[#F7C9C4] bg-white" : "border border-line bg-white",
    to && "hover:-translate-y-0.5 hover:shadow-hover focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary-500/25",
  );
  return to ? (
    <Link to={to} search={search as never} className={cls}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

export function KpiSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,160px),1fr))] gap-3">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="rounded-[18px] border border-line bg-white p-[18px]">
          <Skeleton className="size-9 rounded-full" />
          <Skeleton className="mt-3 h-7 w-16" />
          <Skeleton className="mt-2.5 h-3 w-3/4" />
          <Skeleton className="mt-2 h-2.5 w-full" />
        </div>
      ))}
    </div>
  );
}

/* ───────────────────────── Cards ───────────────────────── */

export function SectionHead({ kicker, title, subtitle, action, className }: { kicker?: string; title: ReactNode; subtitle?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn("mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-3", className)}>
      <div className="min-w-0 flex-[1_1_240px]">
        {kicker && <div className="text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-primary-500">{kicker}</div>}
        <h2 className="text-[17px] font-extrabold tracking-[-0.01em] text-ink">{title}</h2>
        {subtitle && <p className="mt-1 text-[12.5px] leading-relaxed text-muted">{subtitle}</p>}
      </div>
      {action && <div className="flex shrink-0 flex-wrap gap-2">{action}</div>}
    </div>
  );
}

/** « Chargement impossible » card, the board's text: « « Une erreur est survenue » — <what> ». */
export function LoadError({ what, onRetry, message, className }: { what: string; onRetry: () => void; message?: string; className?: string }) {
  return (
    <div role="alert" className={cn("rounded-[18px] border border-[#F7C9C4] bg-[#FFF8F7] px-5 py-8 text-center", className)}>
      <span className="mx-auto mb-3 inline-flex size-11 items-center justify-center rounded-xl bg-danger-bg">
        <AlertCircle className="size-5 text-danger-500" />
      </span>
      <div className="text-[14px] font-extrabold text-ink">Chargement impossible</div>
      <p className="mx-auto mt-1 max-w-md text-[12.5px] leading-relaxed text-muted">
        « {message ?? "Une erreur est survenue"} » — {what}
      </p>
      <Button variant="outline" size="md" className="mt-4" onClick={onRetry}>
        Réessayer
      </Button>
    </div>
  );
}

/** Notice inside a window (violet info, green success, red error, amber warning). */
export function Notice({ tone = "info", children, className, role }: { tone?: "info" | "success" | "danger" | "warning"; children: ReactNode; className?: string; /** A red state that is not an error (« Compte suspendu ») is a status. */ role?: "alert" | "status" }) {
  const tones = {
    info: "border-primary-100 bg-primary-50 text-[#3E38B0]",
    success: "border-success-line bg-success-bg text-success",
    danger: "border-danger-line bg-danger-bg text-danger",
    warning: "border-warning-line bg-warning-bg text-warning",
  };
  const Icon = tone === "success" ? CheckCircle2 : tone === "info" ? null : AlertCircle;
  return (
    <div role={role ?? (tone === "danger" ? "alert" : tone === "success" ? "status" : undefined)} className={cn("flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-[12.5px] leading-relaxed", tones[tone], className)}>
      {Icon && <Icon className="mt-0.5 size-4 shrink-0" />}
      <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">{children}</div>
    </div>
  );
}

/** Read-only form row of a window (« Période », « Contenu »). */
export function ReadField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-[7px] text-xs font-semibold text-ink">{label}</div>
      <div className="flex min-h-[42px] items-center rounded-[10px] border border-line-strong bg-canvas px-3.5 py-2 text-[13px] text-muted [overflow-wrap:anywhere]">{children}</div>
    </div>
  );
}

export function WindowFooter({ children }: { children: ReactNode }) {
  return <div className="sticky bottom-0 z-[1] flex flex-wrap justify-end gap-2.5 border-t border-line bg-white px-6 py-4 max-sm:[&>*]:flex-1">{children}</div>;
}

/* ───────────────────────── Downloads & export window ───────────────────────── */

export function downloadFile(f: ExportFile) {
  const url = URL.createObjectURL(new Blob([f.content], { type: f.mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = f.fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * « Exporter … (CSV) » window of FS-Vue / FS-Comptes / FS-Iopole / FS-Fiche: Ouverte → Génération… → Fichier prêt
 * (download starts, window stays open) or « Export impossible pour le moment. Réessayez. ».
 */
export function ExportWindow({
  open,
  onClose,
  title,
  subtitle,
  fields,
  notice,
  run,
  readyText,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  fields: { label: string; value: ReactNode }[];
  notice: ReactNode;
  run: () => Promise<ExportFile>;
  readyText?: (f: ExportFile) => ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState<ExportFile | null>(null);
  const [error, setError] = useState(false);
  const online = useOnline();
  const close = () => {
    onClose();
    setReady(null);
    setError(false);
  };
  const go = async () => {
    setBusy(true);
    setError(false);
    setReady(null);
    try {
      const f = await run();
      setReady(f);
      downloadFile(f);
    } catch (e) {
      void (e as ApiError);
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Window open={open} onClose={close} width={520} title={title} subtitle={subtitle} icon={<Download className="size-[18px]" />}>
      <div className="space-y-4 px-6 pb-5 pt-5">
        {fields.map((f) => (
          <ReadField key={f.label} label={f.label}>
            {f.value}
          </ReadField>
        ))}
        <Notice>{notice}</Notice>
        {ready && <Notice tone="success">{readyText ? readyText(ready) : <>Fichier prêt : {ready.fileName} ({ready.rows} lignes). Le téléchargement démarre automatiquement.</>}</Notice>}
        {error && <Notice tone="danger">« Export impossible pour le moment. Réessayez. »</Notice>}
      </div>
      <WindowFooter>
        <Button variant="outline" size="lg" onClick={close}>
          Annuler
        </Button>
        <Button size="lg" loading={busy} disabled={!online} onClick={go}>
          {busy ? "Génération…" : "Exporter"}
        </Button>
      </WindowFooter>
    </Window>
  );
}

/** Amount « 1 980,00 € » (TTC unless said). */
export const money = (cents: number) => formatCents(cents);

/* ───────────────────────── URL state ───────────────────────── */

/** Patches the current route's search params (filters replace history; windows push so « Retour » closes them). */
export function useSearchPatch<S extends Record<string, unknown>>() {
  const navigate = useNavigate();
  return (patch: Partial<S>, opts: { push?: boolean } = {}) =>
    void navigate({
      to: ".",
      search: ((prev: S) => {
        const next: Record<string, unknown> = { ...prev, ...patch };
        for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === "" || next[k] === false) delete next[k];
        return next;
      }) as never,
      replace: !opts.push,
      resetScroll: false,
    });
}

/** « Gratuit · depuis le 12/05 » · « Mensuel · renouvelé le 03/10 » · « Annuel · jusqu'au 14/08/2027 » · « Mensuel · expiré le 20/09 ». */
export function periodLabel(r: Pick<SaasAccountRow, "plan" | "cycle" | "subscribedAt" | "paidUntil" | "expiredAt">): string {
  if (r.plan === "start" || !r.cycle) return `Gratuit · depuis le ${fmtDayMonth(r.subscribedAt)}`;
  const c = r.cycle === "annual" ? "Annuel" : "Mensuel";
  if (r.expiredAt) return `${c} · expiré le ${fmtDayMonth(r.expiredAt)}`;
  if (r.cycle === "annual") return `${c} · jusqu'au ${r.paidUntil ? fmtDate(r.paidUntil) : "—"}`;
  return `${c} · renouvelé le ${r.paidUntil ? fmtDayMonth(r.paidUntil) : "—"}`;
}

/* ───────────────────────── Row menu (⋯) ───────────────────────── */

/**
 * Dropdown anchored under its button, right-aligned, rendered `fixed` so a scrolling table never clips it.
 * Click outside, Esc, scroll or choosing an entry closes it.
 */
export function RowMenu({ label, items }: { label: string; items: { label: string; onSelect: () => void; danger?: boolean }[] }) {
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!pos) return;
    const close = () => setPos(null);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    const onDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("[data-row-menu]")) close();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [pos]);
  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={!!pos}
        data-row-menu
        onClick={() => {
          if (pos) return setPos(null);
          const r = btn.current!.getBoundingClientRect();
          setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
        }}
        className="inline-flex size-8 items-center justify-center rounded-lg border border-line bg-white text-ink-2 hover:border-line-strong focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary-500/25"
      >
        <MoreHorizontal className="size-4" />
      </button>
      {pos &&
        createPortal(
          <div
            role="menu"
            data-row-menu
            style={{ top: pos.top, right: pos.right }}
            className="fixed z-50 w-[230px] max-w-[calc(100vw-16px)] rounded-[14px] border border-line bg-white p-[7px] shadow-flyout"
          >
            {items.map((it) => (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                onClick={() => {
                  setPos(null);
                  it.onSelect();
                }}
                className={cn("flex w-full items-center rounded-[9px] px-2.5 py-2 text-left text-[13px] font-semibold hover:bg-canvas", it.danger ? "text-danger" : "text-ink")}
              >
                {it.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
